import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Button,
  Spinner,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { getFormErrors } from 'http-client/apiClient';
import { useCheckIdentifierMutation, useForgetPasswordMutation } from 'http-client/hooks/authHooks';
import {
  ApiResponse,
  CheckIdentifierRequestDto,
  ForgetPasswordDto,
  TenantSelectDto,
} from 'http-client/types/auth.types';

const ForgetPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  // Hooks
  const {
    mutate: checkIdentifier,
    isPending: isCheckingIdentifier,
    error: checkError,
  } = useCheckIdentifierMutation();

  const {
    mutate: forgetPassword,
    isPending: isSendingOtp,
    error: forgetError,
  } = useForgetPasswordMutation();

  // State
  const [step, setStep] = useState<'identifier' | 'tenant-select'>('identifier');
  const [availableTenants, setAvailableTenants] = useState<TenantSelectDto[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [identifier, setIdentifier] = useState<string>('');

  // Step 1: Check Identifier Form
  const identifierForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      emailOrPhone: '',
    } as CheckIdentifierRequestDto,
    validationSchema: Yup.object({
      emailOrPhone: Yup.string().required('Please enter your email or phone number'),
    }),
    onSubmit: (values) => {
      checkIdentifier(values, {
        onSuccess: (response: ApiResponse<TenantSelectDto[]>) => {
          const tenants = response?.data || [];
          setIdentifier(values.emailOrPhone);
          setAvailableTenants(tenants);

          if (tenants.length === 1) {
            // Single tenant → directly send OTP
            localStorage.setItem('tenantId', tenants[0].tenantId);
            const tenantId = tenants[0].tenantId;
            setSelectedTenant(tenantId);
            sendOtp(values.emailOrPhone, tenantId);
          } else if (tenants.length > 1) {
            // Multiple tenants → show selection
            setStep('tenant-select');
          }
        },
      });
    },
  });

  // Function to send OTP (used for both single tenant and after selection)
  const sendOtp = (emailOrPhone: string, tenantIdentifier?: string) => {
    const payload: ForgetPasswordDto = { emailOrPhone };

    forgetPassword(payload, {
      onSuccess: () => {
        navigate('/reset-password', {
          state: {
            emailOrPhone: emailOrPhone,
            tenantIdentifier: tenantIdentifier || selectedTenant,
          },
        });
      },
    });
  };

  // Handle Tenant Selection
  const handleTenantSelect = (tenantIdentifier: string) => {
    setSelectedTenant(tenantIdentifier);
    sendOtp(identifier, tenantIdentifier);
  };

  // Handle Back
  const handleBack = () => {
    setIdentifier('');
    setAvailableTenants([]);
    setSelectedTenant('');
    setStep('identifier');
    identifierForm.resetForm();
  };

  return (
    <React.Fragment>
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center py-5"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              {/* Main Card */}
              <Card
                className="border-0 shadow-lg overflow-hidden"
                style={{ borderRadius: '1.5rem' }}
              >
                {/* Animated gradient header */}
                <div
                  className="position-relative"
                  style={{
                    background: 'linear-gradient(120deg, #4F46E5 0%, #7C3AED 100%)',
                    padding: '2rem 1.5rem',
                    textAlign: 'center',
                  }}
                >
                  <div className="mb-3">
                    <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex p-3">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 2L2 7L12 12L22 7L12 2Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 17L12 22L22 17"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 12L12 17L22 12"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  <h2 className="text-white fw-bold mb-2" style={{ letterSpacing: '-0.02em' }}>
                    Reset Password
                  </h2>
                  <p className="text-white text-opacity-90 mb-0">
                    Enter your email or phone to receive OTP
                  </p>
                </div>

                <CardBody className="p-4 p-lg-5">
                  {/* STEP 1: Enter Email or Phone */}
                  {step === 'identifier' && (
                    <>
                      <div className="text-center mb-4">
                        <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex p-2 mb-3">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                              stroke="#F59E0B"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <h4 className="fw-semibold mb-2">Forgot your password?</h4>
                        <p className="text-muted small">
                          No worries! Enter your email or phone and we will send you an OTP to reset
                          it.
                        </p>
                      </div>

                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          identifierForm.handleSubmit();
                          return false;
                        }}
                      >
                        {(checkError || forgetError) && (
                          <Alert color="danger" className="border-0 rounded-3 mb-4">
                            <div className="d-flex align-items-center">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="me-2"
                              >
                                <path
                                  d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              {getFormErrors(checkError) ||
                                getFormErrors(forgetError) ||
                                'No account found with this identifier.'}
                            </div>
                          </Alert>
                        )}

                        <div className="mb-4">
                          <Label className="form-label fw-semibold text-secondary">
                            Email or Phone Number
                          </Label>
                          <Input
                            name="emailOrPhone"
                            className="form-control-lg border-0 bg-light rounded-3"
                            placeholder="Enter your email or phone number"
                            style={{ backgroundColor: '#F3F4F6' }}
                            onChange={identifierForm.handleChange}
                            onBlur={identifierForm.handleBlur}
                            value={identifierForm.values.emailOrPhone}
                            invalid={
                              identifierForm.touched.emailOrPhone &&
                              !!identifierForm.errors.emailOrPhone
                            }
                          />
                          {identifierForm.touched.emailOrPhone &&
                          identifierForm.errors.emailOrPhone ? (
                            <FormFeedback type="invalid" className="mt-1">
                              {identifierForm.errors.emailOrPhone}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <Button
                          color="primary"
                          type="submit"
                          disabled={isCheckingIdentifier}
                          className="w-100 btn-lg rounded-3 fw-semibold mb-3"
                          style={{
                            background: 'linear-gradient(120deg, #4F46E5 0%, #7C3AED 100%)',
                            border: 'none',
                            boxShadow: '0 4px 14px 0 rgba(79, 70, 229, 0.4)',
                          }}
                        >
                          {isCheckingIdentifier ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Checking...
                            </>
                          ) : (
                            'Send OTP'
                          )}
                        </Button>

                        <div className="text-center">
                          <Link
                            to="/login"
                            className="text-decoration-none small d-inline-flex align-items-center"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="me-1"
                            >
                              <path
                                d="M10 19L3 12M3 12L10 5M3 12H21"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Back to Login
                          </Link>
                        </div>
                      </Form>
                    </>
                  )}

                  {/* STEP 2: Tenant Selection (if multiple) */}
                  {step === 'tenant-select' && (
                    <>
                      <div className="text-center mb-4">
                        <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex p-2 mb-3">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M19 21V19C19 16.2386 16.7614 14 14 14H10C7.23858 14 5 16.2386 5 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                              stroke="#3B82F6"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <h4 className="fw-semibold mb-2">Select Organization</h4>
                        <p className="text-muted small">
                          Multiple accounts found. Please select which one to reset password for.
                        </p>
                      </div>

                      <div className="mb-4">
                        {availableTenants.map((tenant) => (
                          <button
                            key={tenant.tenantId}
                            type="button"
                            onClick={() => handleTenantSelect(tenant.tenantId)}
                            disabled={isSendingOtp}
                            className="w-100 text-start p-3 mb-3 border rounded-3 bg-white transition-all"
                            style={{
                              borderColor: '#E5E7EB',
                              borderRadius: '0.75rem',
                              transition: 'all 0.2s ease',
                              cursor: isSendingOtp ? 'not-allowed' : 'pointer',
                              opacity: isSendingOtp ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!isSendingOtp) {
                                e.currentTarget.style.borderColor = '#4F46E5';
                                e.currentTarget.style.backgroundColor = '#F9FAFB';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#E5E7EB';
                              e.currentTarget.style.backgroundColor = 'white';
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M19 21V19C19 16.2386 16.7614 14 14 14H10C7.23858 14 5 16.2386 5 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                                    stroke="#4F46E5"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="fw-semibold">{tenant.tenantName}</div>
                                <div className="text-muted small">{tenant.tenantId}</div>
                              </div>
                              {isSendingOtp && selectedTenant === tenant.tenantId ? (
                                <Spinner size="sm" color="primary" className="ms-auto" />
                              ) : (
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="ms-auto"
                                >
                                  <path
                                    d="M9 18L15 12L9 6"
                                    stroke="#9CA3AF"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>

                      <Button
                        color="light"
                        onClick={handleBack}
                        disabled={isSendingOtp}
                        className="w-100 btn-lg rounded-3"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="me-2 d-inline-block"
                        >
                          <path
                            d="M10 19L3 12M3 12L10 5M3 12H21"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Back
                      </Button>
                    </>
                  )}
                </CardBody>
              </Card>

              {/* Footer */}
              <div className="text-center mt-4">
                <p className="text-white-50 small mb-0">
                  © {new Date().getFullYear()} Pharmacy Management. All rights reserved.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ForgetPasswordPage;
